<template>
  <v-card
    class="tarjeta-reparacion"
    outlined
    :class="{
      'no-drag': item.Estado === 'finalizado' || disableDragHandling,
      'categoria-completa': categoriaCompleta,
      'resumen-seleccionable': esResumen,
      'cursor-pointer': cursorPointer,
    }"
    @click="handleCardClick"
  >
    <!-- Header: patente + drag handle + estado -->
    <div
      class="card-drag-handle d-flex align-center justify-space-between pa-2 cursor-pointer"
      @click.stop="handleCardClick($event, true)"
    >
      <span class="font-weight-bold text-uppercase patente-font">
        <v-icon v-if="categoriaCompleta" small color="green darken-2" class="mr-1">fas fa-check-circle</v-icon>
        {{ item.Patente || '—' }}
      </span>
      <div class="d-flex align-center">
        <v-tooltip v-if="esResumen" bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              color="primary"
              class="historial-action mr-1"
              aria-label="Ver historial de procesos"
              v-bind="attrs"
              v-on="on"
              @mousedown.stop
              @touchstart.stop
              @click.stop="$emit('ver-historial', item)"
            >
              <v-icon small>fas fa-eye</v-icon>
            </v-btn>
          </template>
          <span>Ver historial de procesos</span>
        </v-tooltip>
        <v-chip
          x-small
          :color="estadoColor"
          label
          class="text-center px-1 white--text"
        >
          {{ estadoLabel }}
        </v-chip>
      </div>
    </div>

    <v-card-text class="pa-2 pt-0">
      <!-- Datos del vehículo -->
      <div class="text-caption grey--text text--darken-1">
        {{ item.Marca }} {{ item.Modelo }}
        <template v-if="item.Anio">— {{ item.Anio }}</template>
        <template v-if="item.Color">— {{ item.Color }}</template>
      </div>
      <div class="text-caption font-weight-bold mt-1">Empresa de origen: {{ nombreEmpresa }}</div>

      <div v-if="item.Ubicacion" class="text-caption mt-1">
        <v-icon x-small class="mr-1">fas fa-map-marker-alt</v-icon>
        {{ item.Ubicacion }}
      </div>

      <div v-if="item.FechaDeIngreso" class="text-caption grey--text text--darken-1 mt-1">
        <v-icon x-small class="mr-1">fas fa-calendar-alt</v-icon>
        Ingreso: {{ formatFecha(item.FechaDeIngreso) }}
      </div>
      <div v-if="item.FechaAgendado && esManoObra && !esGestorRepuestos" class="text-caption blue--text text--darken-2 mt-1">
        <v-icon x-small class="mr-1">fas fa-calendar-check</v-icon>
        Agendado: {{ formatFecha(item.FechaAgendado) }}
      </div>
      <div v-if="item.FechaFinalizado" class="text-caption green--text text--darken-2 mt-1">
        <v-icon x-small class="mr-1">fas fa-check</v-icon>
        Finalizado: {{ formatFecha(item.FechaFinalizado) }}
      </div>

      <!-- Conceptos de la unidad: tres secciones fijas por categoría, sin jerarquía de trabajos -->
      <div v-if="trabajos.length > 0 && !esResumen" class="conceptos-list mt-2">
        <div
          v-for="grupo in gruposUnidad()"
          :key="`cat-${grupo.categoria}`"
          class="item-grupo mb-1"
        >
          <div
            class="text-caption font-weight-bold item-grupo-titulo"
            :class="grupo.textClass"
          >
            {{ grupo.label }}
          </div>
          <div
            v-for="concepto in grupo.items"
            :key="concepto.Id"
            class="item-fila d-flex align-center"
          >
            <v-checkbox
              :input-value="concepto.Completado"
              dense
              hide-details
              class="mt-0 pt-0 mr-1"
              :disabled="!puedeConfirmarItem(concepto.Categoria) || esItemEnProceso(concepto.Id)"
              @change="$emit('completar-item', { trabajoId: concepto.TrabajoId, itemId: concepto.Id, Completado: $event, Categoria: concepto.Categoria })"
            />
            <v-icon v-if="concepto.EsAutomatico" x-small class="mr-1" color="blue-grey">
              fas fa-cogs
            </v-icon>
            <v-progress-circular
              v-if="esItemEnProceso(concepto.Id)"
              indeterminate
              size="16"
              width="2"
              color="info"
              class="mr-1"
            />
            <span
              class="text-caption item-texto flex-grow-1"
              :class="{ 'text-decoration-line-through grey--text': concepto.Completado }"
            >
              {{ concepto.Descripcion }}
            </span>
            <template v-if="puedeGestionarContenido && !concepto.EsAutomatico && puedeMutarItem(concepto.Categoria)">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    @click="$emit('editar-item', { trabajoId: concepto.TrabajoId, itemId: concepto.Id, categoria: concepto.Categoria, descripcion: concepto.Descripcion })"
                  >
                    <v-icon x-small>fas fa-pen</v-icon>
                  </v-btn>
                </template>
                <span>Editar</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    @click="$emit('eliminar-item', { trabajoId: concepto.TrabajoId, itemId: concepto.Id })"
                  >
                    <v-icon x-small>fas fa-times</v-icon>
                  </v-btn>
                </template>
                <span>Eliminar</span>
              </v-tooltip>
            </template>
            <template v-if="puedeComentarItem(concepto)">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    @click="$emit('ver-comentarios', { trabajoId: concepto.TrabajoId, itemId: concepto.Id, descripcion: concepto.Descripcion })"
                  >
                    <v-icon x-small>fas fa-comment</v-icon>
                  </v-btn>
                </template>
                <span>Comentarios</span>
              </v-tooltip>
            </template>
            <template v-if="puedeCrearAplicacion(concepto)">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      icon
                      x-small
                      :color="concepto.AplicacionItemId ? 'success' : 'primary'"
                      :loading="esAplicacionEnProceso(concepto.Id)"
                      :disabled="Boolean(concepto.AplicacionItemId) || esAplicacionEnProceso(concepto.Id)"
                      @click="$emit('crear-aplicacion', { trabajoId: concepto.TrabajoId, itemId: concepto.Id })"
                    >
                      <v-icon x-small>{{ concepto.AplicacionItemId ? 'fas fa-check' : 'fas fa-wrench' }}</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>{{ concepto.AplicacionItemId ? 'Aplicación creada' : 'Crear mano de obra para este repuesto' }}</span>
              </v-tooltip>
            </template>
          </div>
        </div>
      </div>

      <!-- Acción: agregar concepto (crea la reparación si la unidad aún no tiene una) -->
      <v-btn
        v-if="puedeGestionarContenido"
        block
        small
        color="success"
        class="mt-1"
        @click="$emit('agregar-concepto')"
      >
        <v-icon x-small left>fas fa-plus</v-icon>
        Agregar tarea
      </v-btn>

      <!-- Acción: peritaje -->
      <v-btn
        v-if="puedeGestionarContenido"
        block
        small
        :color="item.PeritajeURL ? 'info' : 'teal'"
        outlined
        class="mt-1"
        @click="item.PeritajeURL ? $emit('ver-peritaje', item) : $emit('cargar-peritaje', item)"
      >
        <v-icon x-small left>{{ item.PeritajeURL ? 'fas fa-eye' : 'fas fa-file-upload' }}</v-icon>
        {{ item.PeritajeURL ? 'Ver peritaje' : 'Cargar peritaje' }}
      </v-btn>

      <template v-if="esManoObra">
        <div v-if="turnos.length && !esGestorRepuestos" class="mt-2">
          <div class="text-caption font-weight-bold mb-1">Turnos de la unidad</div>
          <div v-for="turno in turnos" :key="turno.Id" class="text-caption blue--text text--darken-2">
            <v-icon x-small color="blue darken-2" class="mr-1">fas fa-calendar-check</v-icon>
            {{ formatTurno(turno) }}
            <template v-if="turno.TurnoEliminado">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon x-small color="warning" class="ml-1 mr-1" v-bind="attrs" v-on="on">fas fa-exclamation-triangle</v-icon>
                </template>
                <span>Turno borrado</span>
              </v-tooltip>
            </template>
            <template v-else-if="turno.RecepcionistaNombre">
              <v-icon x-small color="blue darken-2" class="ml-1 mr-1">fas fa-user</v-icon>
              {{ turno.RecepcionistaNombre }}
            </template>
          </div>
        </div>

        <div v-if="ordenes.length" class="mt-2">
          <div class="text-caption font-weight-bold mb-1">Órdenes abiertas de la unidad</div>
          <div
            v-for="orden in ordenes"
            :key="orden.OrdenId"
            class="text-caption mb-1"
          >
            N° {{ orden.NumeroOrden }}
          </div>
        </div>
        <v-alert
          v-else-if="ordenesError"
          dense
          text
          type="error"
          class="mt-2 text-caption"
        >
          {{ ordenesError }}
        </v-alert>

        <v-btn
          v-if="ordenes.length || ordenesAnteriores.length"
          block
          small
          color="warning"
          dark
          class="mt-1 text-none"
          @click="$emit('ver-detalle-orden', { item, ordenes, anteriores: ordenesAnteriores, anterioresTotal: ordenesAnterioresTotal })"
        >
          <v-icon x-small left>fas fa-list-alt</v-icon>
          Ver detalle órdenes
        </v-btn>
        <div v-if="ordenesAnterioresTotal" class="text-caption grey--text mt-1">
          {{ ordenesAnterioresTotal }} {{ ordenesAnterioresTotal === 1 ? 'orden anterior registrada' : 'órdenes anteriores registradas' }}
        </div>

        <v-btn
          v-if="puedeGestionar"
          block
          small
          color="info"
          class="mt-1"
          @click="$emit('gestionar-turnos', item)"
        >
          <v-icon x-small left>fas fa-calendar-plus</v-icon>
          Agendar turnos
        </v-btn>
      </template>
    </v-card-text>

    <div v-if="esResumen" class="px-2 pb-2">
      <div class="d-flex justify-space-between text-caption mb-1">
        <span>Progreso</span>
        <strong>{{ porcentajeGlobal }}%</strong>
      </div>
      <v-progress-linear
        :value="porcentajeGlobal"
        color="green"
        height="7"
        rounded
      />
      <v-btn
        v-if="porcentajeGlobal === 100"
        block
        small
        color="success"
        class="mt-2"
        @click.stop="$emit('finalizar', item)"
      >
        Finalizar
      </v-btn>
    </div>

    <!-- Footer: acciones según estado y permisos -->
    <v-card-actions
      v-if="tieneAccionesFooter"
      class="px-2 pt-0 pb-2 d-flex flex-wrap"
    >
      <v-btn
        v-if="puedeAgendarTurno"
        small
        color="info"
        class="ma-1 flex-grow-1"
        @click="$emit('agendar', item)"
      >
        <v-icon x-small left>fas fa-calendar-plus</v-icon>
        Agendar turno
      </v-btn>

      <v-btn
        v-if="puedeGestionar && item.Estado === 'agendado'"
        x-small
        outlined
        color="blue"
        class="text-caption ma-1"
        @click="$emit('reprogramar', item)"
      >
        <v-icon x-small left>fas fa-calendar-alt</v-icon>
        Reprogramar
      </v-btn>

      <v-btn
        v-if="puedeGestionar && item.Estado === 'agendado'"
        x-small
        outlined
        color="orange"
        class="text-caption ma-1"
        @click="$emit('cancelar', item)"
      >
        <v-icon x-small left>fas fa-calendar-times</v-icon>
        Cancelar turno
      </v-btn>

      <v-btn
        v-if="puedeGestionar && item.Estado === 'finalizado'"
        x-small
        outlined
        color="info"
        class="text-caption ma-1"
        @click="$emit('reenviar-email', item)"
      >
        <v-icon x-small left>fas fa-envelope</v-icon>
        Reenviar email
      </v-btn>

      <v-btn
        v-if="puedeGestionar && item.Estado === 'finalizado'"
        x-small
        outlined
        color="grey"
        class="text-caption ma-1"
        @click="$emit('limpiar', item)"
      >
        <v-icon x-small left>fas fa-trash</v-icon>
        Limpiar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from "moment";

export default {
  name: "TarjetaReparacion",
  props: {
    item: {
      type: Object,
      required: true,
    },
    permisos: {
      type: Object,
      default: () => ({ lectura: false, gestion: false, repuestos: false, gestorRepuestos: false, destinatarios: false }),
    },
    disableDragHandling: {
      type: Boolean,
      default: false,
    },
    cursorPointer: {
      type: Boolean,
      default: false,
    },
    categoriaColumna: {
      type: String,
      default: null,
    },
    itemsEnProceso: {
      type: Array,
      default: () => [],
    },
    aplicacionesEnProceso: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      ordenes: [],
      ordenesAnteriores: [],
      ordenesAnterioresTotal: 0,
      ordenesCargando: false,
      ordenesError: null,
      ordenesRequestId: 0,
    };
  },
  computed: {
    trabajos() {
      return this.item.Trabajos || [];
    },
    turnos() {
      return this.item.Turnos || [];
    },
    nombreEmpresa() {
      return { 1: "Dycar", 2: "Eurodycar", 3: "GDB" }[Number(this.item.Empresa ?? 1)] || "Origen desconocido";
    },
    esBase() {
      return !this.item.Proyeccion || this.item.Proyeccion === "recibidos";
    },
    esResumen() {
      return this.item.Proyeccion === "en_proceso";
    },
    esManoObra() {
      return this.item.Proyeccion === "mano_obra";
    },
    esTerceros() {
      return this.item.Proyeccion === "terceros";
    },
    categoriaCompleta() {
      return Boolean(this.item.Proyeccion && !this.esBase && !this.esResumen && this.item.CategoriaCompleta);
    },
    porcentajeGlobal() {
      return Number(this.item.PorcentajeCompletado || 0);
    },
    reparacionFinalizada() {
      return this.item.Estado === "finalizado";
    },
    esGestorRepuestos() {
      return Boolean(this.permisos.gestorRepuestos);
    },
    puedeGestionar() {
      return Boolean(this.permisos.gestion && !this.permisos.gestorRepuestos);
    },
    puedeGestionarContenido() {
      return this.esBase && this.puedeGestionar && !this.reparacionFinalizada;
    },
    puedeConfirmarRepuestos() {
      return Boolean(this.permisos.gestorRepuestos || this.permisos.repuestos || this.puedeGestionar);
    },
    puedeAgendarTurno() {
      const elegibles = this.item.TrabajosElegibles || [];
      const habilitadoPorRepuestos = this.item.Proyeccion === "repuestos"
        ? elegibles.length > 0
        : this.item.AgendarHabilitado;
      return Boolean(
        habilitadoPorRepuestos &&
        !this.esBase &&
        ["recibido", "espera_repuestos"].includes(this.item.Estado) &&
        !this.esResumen &&
        !this.esManoObra &&
        !this.esTerceros &&
        this.puedeGestionar
      );
    },
    tieneAccionesFooter() {
      return (
        (this.esBase && this.puedeGestionar && this.item.Estado === "finalizado") ||
        this.puedeAgendarTurno
      );
    },
    estadoColor() {
      if (this.categoriaCompleta) return "green";
      // Proyección del tablero: color según la columna, no el estado real
      const proyeccionMap = {
        recibidos: "blue-grey",
        repuestos: "amber",
        mano_obra: "blue",
        terceros: "deep-purple",
        en_proceso: "purple",
      };
      if (this.item.Proyeccion) return proyeccionMap[this.item.Proyeccion] || "grey";
      // Fuera del tablero (cards sueltas): color según estado
      const estadoMap = {
        recibido: "blue-grey",
        espera_repuestos: "amber",
        agendado: "blue",
        en_proceso: "purple",
        finalizado: "green",
      };
      return estadoMap[this.item.Estado] || "grey";
    },
    estadoLabel() {
      const proyecciones = {
        recibidos: "Recibido",
        repuestos: "Repuestos",
        mano_obra: "Agendado",
        terceros: "Terceros",
        en_proceso: "En proceso",
      };
      if (this.item.Proyeccion) return proyecciones[this.item.Proyeccion] || this.item.Proyeccion;
      const map = {
        recibido: "Recibido",
        espera_repuestos: "En espera",
        agendado: "Agendado",
        en_proceso: "En proceso",
        finalizado: "Finalizado",
      };
      return map[this.item.Estado] || this.item.Estado;
    },
  },
  watch: {
    esManoObra: {
      immediate: true,
      handler(val) {
        if (val && this.item.ReparacionId) {
          this.fetchOrdenes();
        }
      },
    },
    "item.ReparacionId"(val) {
      if (this.esManoObra && val) {
        this.fetchOrdenes();
      }
    },
  },
  methods: {
    handleCardClick(event, permitirNoResumen = false) {
      if (!this.esResumen && !permitirNoResumen) return;
      let target = event && event.target;
      while (target && target !== event.currentTarget) {
        const tag = String(target.tagName || "").toLowerCase();
        if (tag === "button" || tag === "input" || (target.classList && target.classList.contains("v-input"))) return;
        target = target.parentNode;
      }
      this.$emit("card-click", this.item);
    },
    formatFecha(fecha) {
      if (!fecha) return "";
      return moment(fecha).format("DD/MM/YYYY HH:mm");
    },
    formatTurno(turno) {
      const fecha = turno && turno.FechaTurno ? moment(turno.FechaTurno).format("DD/MM/YYYY") : "—";
      return `${fecha} ${turno && turno.Hora ? String(turno.Hora).slice(0, 5) : ""}`.trim();
    },
    async fetchOrdenes() {
      const requestId = ++this.ordenesRequestId;
      this.ordenesCargando = true;
      this.ordenesError = null;
      try {
        const result = await this.$store.dispatch("usados/getOrdenesServicioReparacion", {
          id: this.item.ReparacionId,
        });
        if (requestId !== this.ordenesRequestId) return;
        if (result && result.exito === 1) {
          this.ordenes = result.data || [];
          this.ordenesAnteriores = result.anteriores || [];
          this.ordenesAnterioresTotal = Number(result.anterioresTotal || 0);
        } else {
          this.ordenesError = result?.message || result?.msj || "Error al cargar órdenes";
          this.ordenes = [];
          this.ordenesAnteriores = [];
          this.ordenesAnterioresTotal = 0;
        }
      } catch (err) {
        if (requestId !== this.ordenesRequestId) return;
        this.ordenesError = err.message || "Error al cargar órdenes";
        this.ordenes = [];
        this.ordenesAnteriores = [];
        this.ordenesAnterioresTotal = 0;
      } finally {
        if (requestId === this.ordenesRequestId) {
          this.ordenesCargando = false;
        }
      }
    },
    categoriaLabel(categoria) {
      const map = {
        repuestos: "Repuestos",
        mano_obra: "Mano de obra",
        terceros: "Trabajos de terceros",
      };
      return map[categoria] || categoria;
    },
    categoriaTextClass(categoria) {
      const map = {
        repuestos: "amber--text text--darken-3",
        mano_obra: "blue--text text--darken-3",
        terceros: "purple--text text--darken-3",
      };
      return map[categoria] || "grey--text";
    },
    // Conceptos unit-flat: todos los ítems técnicos de la unidad, sin filtrar
    // por trabajo, conservando el TrabajoId técnico para las acciones por ítem.
    conceptosUnidad() {
      const conceptos = [];
      for (const trabajo of this.trabajos) {
        for (const item of trabajo.Items || []) {
          conceptos.push({ ...item, TrabajoId: trabajo.Id });
        }
      }
      return conceptos;
    },
    // Tres secciones fijas en orden Repuestos -> Mano de obra -> Trabajos de terceros,
    // agregando los conceptos de todos los trabajos técnicos (incl. legacy con nombre).
    gruposUnidad() {
      const orden = ["repuestos", "mano_obra", "terceros"];
      const gruposPorCat = {};
      for (const concepto of this.conceptosUnidad()) {
        const cat = concepto.Categoria;
        if (!gruposPorCat[cat]) {
          gruposPorCat[cat] = {
            categoria: cat,
            label: this.categoriaLabel(cat),
            textClass: this.categoriaTextClass(cat),
            items: [],
          };
        }
        gruposPorCat[cat].items.push(concepto);
      }
      const todos = orden.map((cat) => gruposPorCat[cat] || {
        categoria: cat,
        label: this.categoriaLabel(cat),
        textClass: this.categoriaTextClass(cat),
        items: [],
      });
      if (this.categoriaColumna) {
        return todos.filter((g) => g.categoria === this.categoriaColumna && g.items.length > 0);
      }
      return todos;
    },
    puedeMutarItem(categoria) {
      if (this.reparacionFinalizada) return false;
      return true;
    },
    puedeConfirmarItem(categoria) {
      if (this.reparacionFinalizada) return false;
      if (categoria === "repuestos") {
        return this.puedeConfirmarRepuestos;
      }
      return this.puedeGestionar;
    },
    // Comentarios por ítem: en cards base los habilita gestión (comportamiento
    // existente); en la proyección repuestos (columna En espera de repuestos)
    // cada repuesto es comentable por quienes operan repuestos.
    puedeComentarItem(concepto) {
      if (this.reparacionFinalizada || concepto.EsAutomatico) return false;
      if (this.esBase) {
        return this.puedeGestionarContenido && this.puedeMutarItem(concepto.Categoria);
      }
      if (this.item.Proyeccion === "repuestos" && concepto.Categoria === "repuestos") {
        return this.puedeConfirmarRepuestos;
      }
      return false;
    },
    // El icono de aplicación individual solo se muestra cuando el repuesto
    // está confirmado (check): primero se marca el repuesto, luego se crea la
    // mano de obra para ese repuesto.
    puedeCrearAplicacion(concepto) {
      return Boolean(
        !this.reparacionFinalizada &&
        this.item.Proyeccion === "repuestos" &&
        concepto.Categoria === "repuestos" &&
        concepto.Completado &&
        this.puedeConfirmarRepuestos
      );
    },
    claveItem(itemId) {
      if (itemId === null || itemId === undefined) return null;
      const tieneReparacion = this.item.ReparacionId !== null && this.item.ReparacionId !== undefined && this.item.ReparacionId !== "";
      const entidad = tieneReparacion ? "reparacion" : "unidad";
      const entidadId = tieneReparacion
        ? this.item.ReparacionId
        : `${Number(this.item.Empresa ?? 1)}:${Number(this.item.UsadoID)}`;
      const proyeccion = this.item.Proyeccion || "recibidos";
      if (entidadId === null || entidadId === undefined || entidadId === "") return null;
      return `${entidad}:${entidadId}|proyeccion:${proyeccion}|item:${itemId}`;
    },
    esItemEnProceso(itemId) {
      const clave = this.claveItem(itemId);
      return Boolean(clave) && this.itemsEnProceso.indexOf(clave) !== -1;
    },
    esAplicacionEnProceso(itemId) {
      const clave = this.claveItem(itemId);
      return Boolean(clave) && this.aplicacionesEnProceso.indexOf(clave) !== -1;
    },
  },
};
</script>

<style scoped>
.tarjeta-reparacion {
  cursor: default;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: 0 auto 8px;
  overflow-wrap: anywhere;
}

.categoria-completa {
  background-color: rgba(76, 175, 80, 0.2) !important;
  border-color: #43a047 !important;
}

.resumen-seleccionable {
  cursor: pointer;
}

.cursor-pointer {
  cursor: pointer !important;
}

.cursor-pointer .card-drag-handle {
  cursor: pointer !important;
}

.card-drag-handle {
  cursor: grab;
  background-color: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.card-drag-handle:active {
  cursor: grabbing;
}

.historial-action {
  cursor: pointer !important;
}

.no-drag .card-drag-handle {
  cursor: default;
}

.conceptos-list {
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
  padding-top: 4px;
}

.item-fila {
  min-height: 26px;
}

.item-texto {
  line-height: 1.3;
}

.item-grupo-titulo {
  line-height: 1.3;
}

.patente-font {
  font-size: 1.5rem !important;
}

@media (max-width: 959px) {
  .tarjeta-reparacion .v-btn {
    min-height: 44px;
  }

  .tarjeta-reparacion .v-btn.v-btn--icon {
    width: 44px;
    min-width: 44px;
    height: 44px;
  }

  .card-drag-handle {
    gap: 8px;
  }

  .patente-font {
    min-width: 0;
    overflow-wrap: anywhere;
  }
}
</style>
